import { useEffect, useMemo, useRef, useState } from 'react'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Link } from 'react-router-dom'

import {
  Button,
  ErrorBanner,
  Input,
  errorMessage,
  Modal,
  Select,
  STATUS_LABELS,
  TASK_STATUSES,
  cn,
  pluralize,
  type ColumnRow,
  type ColumnsResponse,
  type TaskStatus,
} from '@/features/issue'
import type { UUID } from '@/types/api'

import {
  columnsForStatus,
  useColumnMutations,
  useLastColumnError,
} from './useColumns'

/**
 * The columns section of project settings — the part the owner actually asked
 * for. Rename, re-map, limit, reorder, add, delete.
 *
 * THE LEGACY MAPPING IS SHOWN, NOT HIDDEN. Every column carries one of the
 * three statuses the iPad app and the MCP server understand, and a person who
 * cannot see that mapping cannot understand why "In Review" shows up as
 * In Progress on their iPad, or why the last To Do column refuses to be
 * deleted. So the mapping is a visible control on every row, with the
 * consequence spelled out above it.
 *
 * Ordering is a drag, and it is also a keyboard operation: the handle is a real
 * button, dnd-kit's keyboard sensor moves the row with the arrow keys, and the
 * new order is committed as one request the server applies in a transaction.
 */
export function ColumnsCard({
  slug,
  projectKey,
  columns,
  canEdit,
  upgradeHref,
}: {
  slug: string | null
  projectKey: string | null
  columns: ColumnsResponse
  canEdit: boolean
  upgradeHref: string | null
}) {
  const mutations = useColumnMutations(slug, projectKey)
  const { error, clear } = useLastColumnError(mutations)
  const [adding, setAdding] = useState(false)
  const [deleting, setDeleting] = useState<ColumnRow | null>(null)

  const rows = columns.data
  const ids = useMemo(() => rows.map((column) => column.id), [rows])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const from = ids.indexOf(String(active.id) as UUID)
    const to = ids.indexOf(String(over.id) as UUID)
    if (from === -1 || to === -1) return
    mutations.reorder.mutate(arrayMove(ids, from, to))
  }

  return (
    <section className="rounded-card border border-ink-200 bg-white">
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-ink-200 px-5 py-4">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold tracking-tight text-ink-900">Columns</h2>
          <p className="mt-0.5 text-sm text-ink-500">
            {pluralize(rows.length, 'column')} on this board, in board order.
          </p>
        </div>
        {canEdit ? (
          columns.can_add_column ? (
            <Button variant="primary" size="sm" onClick={() => setAdding(true)}>
              Add column
            </Button>
          ) : (
            <UpgradeToAdd href={upgradeHref} />
          )
        ) : null}
      </header>

      <p className="border-b border-ink-200 bg-brand-50 px-5 py-3 text-[13px] leading-relaxed text-brand-900">
        Every column maps to one of three statuses — <strong>To Do</strong>,{' '}
        <strong>In Progress</strong>, <strong>Done</strong>. That mapping is what the iPad app and
        the MCP server see, so a card in “In Review” reads as <em>In Progress</em> there. Each
        status keeps at least one column, and the one marked <strong>Default</strong> is where
        those apps write.
      </p>

      {error ? (
        <div
          role="alert"
          className="mx-5 mt-4 flex items-start gap-3 rounded-card border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800"
        >
          <span className="min-w-0 flex-1">{errorMessage(error)}</span>
          <button
            type="button"
            onClick={clear}
            className="shrink-0 rounded-md px-2 py-0.5 text-xs font-medium underline-offset-2 hover:underline"
          >
            Dismiss
          </button>
        </div>
      ) : null}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <ul className="divide-y divide-ink-200">
            {rows.map((column) => (
              <ColumnRowItem
                key={column.id}
                column={column}
                canEdit={canEdit}
                lastOfStatus={columnsForStatus(columns, column.legacy_status) <= 1}
                busy={mutations.patch.isPending || mutations.reorder.isPending}
                onPatch={(input) => mutations.patch.mutate({ id: column.id, input })}
                onDelete={() => setDeleting(column)}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>

      <AddColumnDialog
        open={adding}
        onClose={() => setAdding(false)}
        pending={mutations.create.isPending}
        error={mutations.create.error}
        onSubmit={(input) =>
          mutations.create.mutate(input, { onSuccess: () => setAdding(false) })
        }
      />

      <DeleteColumnDialog
        column={deleting}
        columns={rows}
        onClose={() => setDeleting(null)}
        pending={mutations.remove.isPending}
        error={mutations.remove.error}
        onConfirm={(moveTo) => {
          if (!deleting) return
          mutations.remove.mutate(
            { id: deleting.id, moveTo },
            { onSuccess: () => setDeleting(null) },
          )
        }}
      />
    </section>
  )
}

/**
 * The Add-column affordance on a plan that has none.
 *
 * It is a link to billing rather than a disabled button: a control that only
 * ever answers "no" teaches nothing, and the server would refuse this with a
 * 402 whose whole point is that there is something to do about it.
 */
function UpgradeToAdd({ href }: { href: string | null }) {
  return (
    <div className="text-right">
      {href ? (
        <Link
          to={href}
          className="inline-flex items-center rounded-md bg-brand-600 px-2.5 py-1.5 text-xs font-medium text-white transition hover:bg-brand-700"
        >
          Upgrade to add columns
        </Link>
      ) : (
        <span className="text-xs font-medium text-ink-600">
          Custom columns are a paid feature.
        </span>
      )}
      <p className="mt-1 max-w-[22rem] text-xs text-ink-500">
        Renaming these three and setting WIP limits stays free.
      </p>
    </div>
  )
}

/* ---------------------------------------------------------------------------
 * One row
 * ------------------------------------------------------------------------ */

function ColumnRowItem({
  column,
  canEdit,
  lastOfStatus,
  busy,
  onPatch,
  onDelete,
}: {
  column: ColumnRow
  canEdit: boolean
  lastOfStatus: boolean
  busy: boolean
  onPatch: (input: { name?: string; legacy_status?: TaskStatus; wip_limit?: number | null; is_default_for_status?: boolean }) => void
  onDelete: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.id,
    disabled: !canEdit,
  })

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn('bg-white px-3 py-3 sm:px-5', isDragging && 'opacity-60 shadow-sm')}
    >
      <div className="flex flex-wrap items-end gap-3">
        <button
          {...attributes}
          {...listeners}
          type="button"
          disabled={!canEdit}
          aria-label={`Reorder ${column.name}`}
          title="Drag, or focus and use the arrow keys"
          className={cn(
            'mb-1 shrink-0 rounded-md p-1.5 text-ink-400 transition',
            canEdit ? 'cursor-grab hover:bg-ink-100 hover:text-ink-700' : 'cursor-default opacity-40',
          )}
        >
          <svg viewBox="0 0 16 16" className="size-4" aria-hidden="true">
            <path
              d="M6 4h.01M6 8h.01M6 12h.01M10 4h.01M10 8h.01M10 12h.01"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <NameField column={column} canEdit={canEdit} onCommit={(name) => onPatch({ name })} />

        <label className="w-40 shrink-0 text-xs font-medium text-ink-600">
          <span className="mb-1 block">Shows as</span>
          <Select
            value={column.legacy_status}
            disabled={!canEdit || lastOfStatus || busy}
            title={
              lastOfStatus
                ? `This is the only ${STATUS_LABELS[column.legacy_status]} column. Add another one before moving this one.`
                : 'What the iPad app and the MCP server see for cards in this column'
            }
            onChange={(event) => onPatch({ legacy_status: event.target.value as TaskStatus })}
            className="py-1 text-sm"
          >
            {TASK_STATUSES.map((status) => (
              <option key={status} value={status}>
                {STATUS_LABELS[status]}
              </option>
            ))}
          </Select>
        </label>

        <WipField column={column} canEdit={canEdit} onCommit={(wip) => onPatch({ wip_limit: wip })} />

        <div className="w-16 shrink-0 text-xs font-medium text-ink-600">
          <span className="mb-1 block">Issues</span>
          <span className="block py-1 text-sm tabular-nums text-ink-800">{column.issue_count}</span>
        </div>

        {/* Fixed width so the rows line up: a row showing "Make default"
            is wider than one showing the Default badge, and a ragged right
            edge makes a list of near-identical rows hard to scan. */}
        <div className="mb-0.5 ml-auto flex w-48 shrink-0 items-center justify-end gap-2">
          {column.is_default_for_status ? (
            <span
              className="inline-flex items-center rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700"
              title={`Writes of ${STATUS_LABELS[column.legacy_status]} from the iPad app land here`}
            >
              Default
            </span>
          ) : canEdit ? (
            <Button
              size="sm"
              disabled={busy}
              onClick={() => onPatch({ is_default_for_status: true })}
              title={`Make this where ${STATUS_LABELS[column.legacy_status]} writes from the iPad app land`}
            >
              Make default
            </Button>
          ) : null}

          {canEdit ? (
            <Button
              size="sm"
              variant="danger"
              disabled={lastOfStatus}
              onClick={onDelete}
              title={
                lastOfStatus
                  ? `Every status needs a column. This is the only ${STATUS_LABELS[column.legacy_status]} one.`
                  : `Delete ${column.name}`
              }
            >
              Delete
            </Button>
          ) : null}
        </div>
      </div>

      {lastOfStatus ? (
        // Short on purpose: on a three-column board this line appears three
        // times, and the paragraph explaining WHY already sits at the top of
        // the card. The disabled controls carry the full sentence as a tooltip.
        <p className="mt-1.5 pl-10 text-xs text-ink-500">
          Only {STATUS_LABELS[column.legacy_status]} column — the board keeps one per status.
        </p>
      ) : null}
    </li>
  )
}

/**
 * Inline rename.
 *
 * The field holds a local draft so typing is never fighting a refetch, commits
 * on blur or Enter, and abandons on Escape. An unchanged or emptied value
 * commits nothing: an empty name is a 422 the server would be right to send,
 * and there is no reason to send it.
 */
function NameField({
  column,
  canEdit,
  onCommit,
}: {
  column: ColumnRow
  canEdit: boolean
  onCommit: (name: string) => void
}) {
  const [draft, setDraft] = useState(column.name)
  const committed = useRef(column.name)

  useEffect(() => {
    committed.current = column.name
    setDraft(column.name)
  }, [column.name])

  function commit() {
    const next = draft.trim()
    if (!next || next === committed.current) {
      setDraft(committed.current)
      return
    }
    committed.current = next
    onCommit(next)
  }

  return (
    <label className="min-w-40 flex-1 text-xs font-medium text-ink-600">
      <span className="mb-1 block">Name</span>
      <Input
        value={draft}
        disabled={!canEdit}
        maxLength={64}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            event.currentTarget.blur()
          }
          if (event.key === 'Escape') {
            setDraft(committed.current)
            event.currentTarget.blur()
          }
        }}
        className="py-1 text-sm"
      />
    </label>
  )
}

/**
 * The WIP limit.
 *
 * Empty means no limit, and that is a real value rather than "unchanged" — so
 * clearing the field sends an explicit null. The limit is advisory: the board
 * shows a column at or over it, and the drop still goes through.
 */
function WipField({
  column,
  canEdit,
  onCommit,
}: {
  column: ColumnRow
  canEdit: boolean
  onCommit: (limit: number | null) => void
}) {
  const stored = column.wip_limit == null ? '' : String(column.wip_limit)
  const [draft, setDraft] = useState(stored)
  const committed = useRef(stored)

  useEffect(() => {
    committed.current = stored
    setDraft(stored)
  }, [stored])

  function commit() {
    const next = draft.trim()
    if (next === committed.current) return
    if (next === '') {
      committed.current = ''
      onCommit(null)
      return
    }
    const value = Number(next)
    if (!Number.isInteger(value) || value < 1) {
      setDraft(committed.current)
      return
    }
    committed.current = String(value)
    setDraft(String(value))
    onCommit(value)
  }

  return (
    <label className="w-28 shrink-0 text-xs font-medium text-ink-600">
      <span className="mb-1 block">WIP limit</span>
      <Input
        type="number"
        min={1}
        step={1}
        inputMode="numeric"
        value={draft}
        disabled={!canEdit}
        placeholder="None"
        title="A soft cap. The board flags a column at or over it; drops are never blocked."
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            event.currentTarget.blur()
          }
          if (event.key === 'Escape') {
            setDraft(committed.current)
            event.currentTarget.blur()
          }
        }}
        className="py-1 text-sm"
      />
    </label>
  )
}

/* ---------------------------------------------------------------------------
 * Add
 * ------------------------------------------------------------------------ */

function AddColumnDialog({
  open,
  onClose,
  pending,
  error,
  onSubmit,
}: {
  open: boolean
  onClose: () => void
  pending: boolean
  error: unknown
  onSubmit: (input: { name: string; legacy_status: TaskStatus; wip_limit?: number | null }) => void
}) {
  const [name, setName] = useState('')
  const [status, setStatus] = useState<TaskStatus>('in_progress')
  const [wip, setWip] = useState('')

  useEffect(() => {
    if (!open) {
      setName('')
      setStatus('in_progress')
      setWip('')
    }
  }, [open])

  const trimmed = name.trim()
  const limit = wip.trim() === '' ? null : Number(wip)
  const limitValid = limit === null || (Number.isInteger(limit) && limit >= 1)

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add a column"
      description="It is appended to the end of the board. Drag it where you want it afterwards."
      footer={
        <>
          <Button onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!trimmed || !limitValid || pending}
            onClick={() => onSubmit({ name: trimmed, legacy_status: status, wip_limit: limit })}
          >
            {pending ? 'Adding…' : 'Add column'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <label className="block text-xs font-medium text-ink-600">
          <span className="mb-1 block">Name</span>
          <Input
            value={name}
            maxLength={64}
            placeholder="In Review"
            onChange={(event) => setName(event.target.value)}
          />
        </label>

        <label className="block text-xs font-medium text-ink-600">
          <span className="mb-1 block">Shows as</span>
          <Select value={status} onChange={(event) => setStatus(event.target.value as TaskStatus)}>
            {TASK_STATUSES.map((entry) => (
              <option key={entry} value={entry}>
                {STATUS_LABELS[entry]}
              </option>
            ))}
          </Select>
          <span className="mt-1 block font-normal text-ink-500">
            Cards in this column report this status to the iPad app and the MCP server. Pick the
            one that is closest to true; it is not cosmetic.
          </span>
        </label>

        <label className="block text-xs font-medium text-ink-600">
          <span className="mb-1 block">WIP limit</span>
          <Input
            type="number"
            min={1}
            step={1}
            inputMode="numeric"
            value={wip}
            placeholder="None"
            onChange={(event) => setWip(event.target.value)}
          />
          <span className="mt-1 block font-normal text-ink-500">
            Optional. The board flags the column at or over the limit; it never blocks a drop.
          </span>
        </label>

        {error ? <ErrorBanner error={error} /> : null}
      </div>
    </Modal>
  )
}

/* ---------------------------------------------------------------------------
 * Delete
 * ------------------------------------------------------------------------ */

/**
 * Deleting a column asks where its issues go, and says how many there are.
 *
 * The server would pick the status default on its own, but a person deleting a
 * column with fourteen cards in it deserves to choose — and to be told that
 * moving them to a column with a different status changes what those cards
 * report to the iPad.
 */
function DeleteColumnDialog({
  column,
  columns,
  onClose,
  pending,
  error,
  onConfirm,
}: {
  column: ColumnRow | null
  columns: ColumnRow[]
  onClose: () => void
  pending: boolean
  error: unknown
  onConfirm: (moveTo: UUID | null) => void
}) {
  const candidates = useMemo(
    () => columns.filter((entry) => entry.id !== column?.id),
    [columns, column],
  )
  const suggested = useMemo(() => {
    if (!column) return null
    const sameStatus = candidates.filter((entry) => entry.legacy_status === column.legacy_status)
    const preferred = sameStatus.find((entry) => entry.is_default_for_status) ?? sameStatus[0]
    return preferred?.id ?? candidates[0]?.id ?? null
  }, [candidates, column])

  const [moveTo, setMoveTo] = useState<UUID | null>(suggested)

  useEffect(() => {
    setMoveTo(suggested)
  }, [suggested])

  const destination = candidates.find((entry) => entry.id === moveTo) ?? null
  const changesStatus =
    column !== null && destination !== null && destination.legacy_status !== column.legacy_status

  return (
    <Modal
      open={column !== null}
      onClose={onClose}
      title={column ? `Delete “${column.name}”` : 'Delete column'}
      description="Nothing is deleted except the column itself."
      footer={
        <>
          <Button onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={pending || (column !== null && column.issue_count > 0 && !moveTo)}
            onClick={() => onConfirm(moveTo)}
          >
            {pending ? 'Deleting…' : 'Delete column'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-ink-600">
          {column && column.issue_count > 0
            ? `${pluralize(column.issue_count, 'issue')} will move to another column.`
            : 'This column is empty.'}
        </p>

        {column && column.issue_count > 0 ? (
          <label className="block text-xs font-medium text-ink-600">
            <span className="mb-1 block">Move issues to</span>
            <Select
              value={moveTo ?? ''}
              onChange={(event) => setMoveTo(event.target.value as UUID)}
            >
              {candidates.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.name} — shows as {STATUS_LABELS[entry.legacy_status]}
                </option>
              ))}
            </Select>
          </label>
        ) : null}

        {changesStatus && column ? (
          <p className="rounded-card border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-900">
            These issues currently report <strong>{STATUS_LABELS[column.legacy_status]}</strong>.
            Moving them to “{destination?.name}” changes that to{' '}
            <strong>{destination ? STATUS_LABELS[destination.legacy_status] : ''}</strong>{' '}
            everywhere, including the iPad app.
          </p>
        ) : null}

        {error ? <ErrorBanner error={error} /> : null}
      </div>
    </Modal>
  )
}
